import {
  AlertTriangle,
  Boxes,
  Globe2,
  Clock,
  Factory,
  Beaker,
  Wheat,
  Building2,
  Users,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import { PrimaryCTA, SecondaryCTA } from "@/components/CTAButtons";

const CHALLENGES = [
  {
    icon: Boxes,
    title: "Fragmented sourcing",
    body:
      "Specs scattered across regions, brokers, and inboxes — no single picture of who can actually deliver.",
  },
  {
    icon: AlertTriangle,
    title: "Limited supplier visibility",
    body:
      "Open directories surface noise. You still don't know who is qualified, active, or worth a meeting.",
  },
  {
    icon: Clock,
    title: "Too much time on unqualified outreach",
    body:
      "Procurement teams burn weeks chasing leads that fail on certification, volume, or geography.",
  },
  {
    icon: Globe2,
    title: "Cross-border validation difficulty",
    body:
      "Different regulatory regimes, different cultures, different risks. Validating from a desk doesn't scale.",
  },
];

const PROFILES = [
  {
    Icon: Beaker,
    label: "Nutraceuticals",
    body: "Functional, botanical, and supplement-grade ingredient programs.",
  },
  {
    Icon: Wheat,
    label: "Food & Beverage",
    body: "Specialty and clean-label ingredient sourcing for branded and private label.",
  },
  {
    Icon: Factory,
    label: "Animal Feed",
    body: "Feed-grade ingredients and additives for premium nutrition portfolios.",
  },
  {
    Icon: Building2,
    label: "Contract Manufacturers",
    body: "Co-manufacturers who need pre-vetted suppliers to support new wins.",
  },
  {
    Icon: Users,
    label: "Multi-site Procurement",
    body: "Procurement teams consolidating across plants, regions, or BUs.",
  },
];

export default function ForBuyers() {
  return (
    <div data-testid="for-buyers-page">
      <PageHero
        eyebrow="For Buyers"
        title={
          <>
            Pre-qualified supplier opportunities,
            <br />
            <span className="text-brand-olive">delivered as a report</span>.
          </>
        }
        subtitle="IngredientSignal gives sourcing teams visibility into the right counterparties — without the noise of open directories or the risk of unverified outreach."
      >
        <div className="flex flex-wrap gap-4">
          <PrimaryCTA to="/get-started?audience=buyer" testId="buyers-hero-cta">
            Request a Buyer Report
          </PrimaryCTA>
          <SecondaryCTA to="/platform" testId="buyers-hero-secondary">
            How the platform supports this
          </SecondaryCTA>
        </div>
      </PageHero>

      {/* Buyer challenges */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="Buyer Challenges"
            title="Sourcing specialty ingredients is harder than the directories admit."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CHALLENGES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  data-testid={`buyers-challenge-${c.title.toLowerCase().split(" ")[0]}`}
                  className="bg-brand-ivory border border-brand-border p-10 hover:border-brand-muted/40 transition-colors"
                >
                  <Icon
                    className="w-6 h-6 text-brand-olive"
                    strokeWidth={1.4}
                  />
                  <h3 className="font-display mt-6 text-xl text-brand-deep font-medium">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-brand-muted leading-relaxed font-light">
                    {c.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What buyers get */}
      <section className="py-24 lg:py-32 bg-brand-linen border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="What Buyers Get"
              title="A written, verified shortlist — built around your specs."
              intro="FFIB analysts translate your sourcing question into a deliverable you can put in front of leadership."
            />
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-brand-border border-y border-brand-border">
              {[
                "A curated supplier shortlist matched to your specs and certifications",
                "A written sourcing intelligence report on the category",
                "Cross-border validation notes on counterparty risk",
                "Warm introductions to the right counterparts at each supplier",
                "Ongoing analyst availability for follow-up questions",
              ].map((line, idx) => (
                <li
                  key={line}
                  data-testid={`buyers-get-${idx}`}
                  className="py-6 flex items-start gap-6"
                >
                  <span className="font-mono text-xs text-brand-olive tracking-[0.2em] pt-1">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="text-brand-warm leading-relaxed font-light">
                    {line}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Ideal profile */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="Ideal Buyer Profile"
            title="Built for sourcing teams who need answers, not access."
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border">
            {PROFILES.map((p) => (
              <div
                key={p.label}
                data-testid={`buyers-profile-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-brand-cream p-8 hover:bg-brand-ivory transition-colors"
              >
                <p.Icon
                  className="w-6 h-6 text-brand-olive"
                  strokeWidth={1.4}
                />
                <h3 className="font-display mt-6 text-lg text-brand-deep font-medium">
                  {p.label}
                </h3>
                <p className="mt-3 text-sm text-brand-muted leading-relaxed font-light">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-brand-border bg-brand-linen/70">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-deep font-light leading-tight">
            Hand us the sourcing brief.
          </h2>
          <p className="mt-5 text-brand-muted max-w-2xl font-light">
            We'll return a verified shortlist and host the introductions.
          </p>
          <div className="mt-8">
            <PrimaryCTA to="/get-started?audience=buyer" testId="buyers-bottom-cta">
              Request a Buyer Report
            </PrimaryCTA>
          </div>
        </div>
      </section>
    </div>
  );
}
